import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { MantineProvider, createTheme } from '@mantine/core';
import '@mantine/core/styles.css';
import SupervisoryOverview from './pages/SupervisoryOverview';
import ModelsPage from './pages/ModelsPage';
import ClimatePage from './pages/ClimatePage';
import IncidentsPage from './pages/IncidentsPage';
import VendorsPage from './pages/VendorsPage';
import ReturnsPage from './pages/ReturnsPage';
import KnowledgePage from './pages/KnowledgePage';
import DocumentsPage from './pages/DocumentsPage';
import GuidancePage from './pages/GuidancePage';
import SuperintendentCommandCenter from './pages/SuperintendentCommandCenter';
import SupervisorWorkspace from './pages/SupervisorWorkspace';
import AnalystWorkspace from './pages/AnalystWorkspace';
import FIConsoleDashboard from './pages/FIConsoleDashboard';
import GuidelineDetailPage from './pages/GuidelineDetailPage';
import HowItWorksPage from './pages/HowItWorksPage';
import ApproachPage from './pages/ApproachPage';
import ConsoleStatsPage from './pages/ConsoleStatsPage';
import VisitorAnalyticsPage from './pages/VisitorAnalyticsPage';
import MemoryGraphPage from './pages/MemoryGraphPage';
import HoldcoPage from './pages/HoldcoPage';
import PanagoPage from './pages/PanagoPage';
import PhantomScreensPage from './pages/PhantomScreensPage';
import ApexFoodPage from './pages/ApexFoodPage';
import DAAPage from './pages/DAAPage';
import ConsolePanel from './components/ConsolePanel';
import CommandPalette from './components/CommandPalette';
import { FilterProvider, useFilters } from './contexts/FilterContext';

const theme = createTheme({
  primaryColor: 'blue',
});

function Sidebar() {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isModulesExpanded, setIsModulesExpanded] = useState(true);

  const dashboardPath = '/';
  const isDashboardActive = location.pathname === dashboardPath;

  const moduleItems = [
    { path: '/holdco', label: 'HoldCo', sublabel: 'Portfolio Overview', icon: '🏢' },
    { path: '/panago', label: 'Panago Pizza', sublabel: '168 Locations', icon: '🍕' },
    { path: '/phantom-screens', label: 'Phantom Screens', sublabel: '245 Dealers', icon: '🪟' },
    { path: '/apex-food', label: 'Apex Food Source', sublabel: '48 Clients', icon: '🥗' },
    { path: '/approach', label: 'Approach', sublabel: 'Comparison', icon: '🔀' },
    { path: '/how-it-works', label: 'How It Works', sublabel: 'Flow', icon: '🔄' },
    { path: '/console-stats', label: 'Console Stats', sublabel: 'Live Metrics', icon: '📊' },
  ];

  const isAnyModuleActive = moduleItems.some((item) => location.pathname === item.path);

  return (
    <div
      className={`${isCollapsed ? 'w-20' : 'w-64'} bg-blue-700 text-white h-screen fixed left-0 top-0 transition-all duration-300 flex flex-col shadow-xl`}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Logo/Header */}
      <div className="p-6 border-b border-blue-800">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div>
              <h1 className="text-xl font-bold">Eclipse</h1>
              <p className="text-xs text-blue-200 mt-1">Kenbri Group</p>
            </div>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-white hover:bg-blue-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!isCollapsed}
          >
            {isCollapsed ? '→' : '←'}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto" aria-label="Dashboard navigation">
        {/* Dashboard Top-Level Item */}
        <div>
          <div className="flex items-center">
            <Link
              to={dashboardPath}
              className={`flex-1 flex items-center px-4 py-3 rounded-lg transition-colors ${
                isDashboardActive && !isAnyModuleActive
                  ? 'bg-blue-800 text-white'
                  : 'text-blue-100 hover:bg-blue-800 hover:text-white'
              }`}
              aria-current={isDashboardActive && !isAnyModuleActive ? 'page' : undefined}
            >
              <span className="text-2xl" aria-hidden="true">📊</span>
              {!isCollapsed && (
                <div className="ml-3">
                  <div className="font-medium">Dashboard</div>
                </div>
              )}
            </Link>
            {!isCollapsed && (
              <button
                onClick={() => setIsModulesExpanded(!isModulesExpanded)}
                className="px-2 py-3 text-blue-100 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
                aria-label={isModulesExpanded ? 'Collapse modules' : 'Expand modules'}
                aria-expanded={isModulesExpanded}
                aria-controls="dashboard-modules"
              >
                <span className="text-sm">{isModulesExpanded ? '▼' : '▶'}</span>
              </button>
            )}
          </div>

          {/* Nested Modules */}
          {!isCollapsed && isModulesExpanded && (
            <div
              id="dashboard-modules"
              className="ml-4 mt-1 space-y-1 border-l-2 border-blue-700 pl-2"
              role="group"
              aria-label="Dashboard modules"
            >
              {moduleItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center px-3 py-2 rounded-lg transition-colors text-sm ${
                      isActive
                        ? 'bg-blue-800 text-white'
                        : 'text-blue-100 hover:bg-blue-800 hover:text-white'
                    }`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span className="text-lg" aria-hidden="true">{item.icon}</span>
                    <div className="ml-2">
                      <div className="font-medium">{item.label}</div>
                      {item.sublabel && (
                        <div className="text-xs text-blue-200">{item.sublabel}</div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </nav>

      {/* Footer */}
      {!isCollapsed && (
        <div className="p-4 border-t border-blue-800">
          <div className="text-xs text-blue-200">
            <div className="font-medium mb-1">Powered by</div>
            <div>Zeus Memory</div>
            <div className="mt-2 text-blue-300">ALDC Internal</div>
          </div>
        </div>
      )}
    </div>
  );
}

function AppLayout() {
  const [isConsoleOpen, setIsConsoleOpen] = useState(false);
  const filters = useFilters();
  const location = useLocation();

  const isFullScreenView = location.pathname === '/superintendent' || location.pathname === '/supervisor' || location.pathname === '/analyst' || location.pathname === '/' || location.pathname === '/fi-console' || location.pathname === '/how-it-works' || location.pathname === '/approach' || location.pathname === '/memory-graph' || location.pathname === '/holdco' || location.pathname === '/panago' || location.pathname === '/phantom-screens' || location.pathname === '/apex-food' || location.pathname === '/daa';

  return (
    <div className="flex min-h-screen bg-gray-50">
      {!isFullScreenView && <Sidebar />}

      {/* Main Content Area */}
      <div className={`flex-1 transition-all duration-300 ${!isFullScreenView ? 'ml-64' : ''}`}>
        {/* Top Bar */}
        {!isFullScreenView && (
          <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
            <div className="px-8 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-800">
                    Kenbri Group Portfolio
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Panago Pizza | Phantom Screens | Apex Food Source
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-600 flex items-center space-x-2">
                    <span className="font-medium">Viewing:</span>
                    <select
                      value={filters.selectedInstitution}
                      onChange={(e) => filters.setSelectedInstitution(e.target.value)}
                      className="bg-white border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Companies</option>
                      <optgroup label="Operating Companies">
                        <option value="panago">Panago Pizza</option>
                        <option value="phantom">Phantom Screens</option>
                        <option value="apex">Apex Food Source</option>
                      </optgroup>
                    </select>
                  </div>
                  <div className="w-3 h-3 bg-green-500 rounded-full" title="Systems Operational"></div>
                </div>
              </div>
            </div>
          </header>
        )}

        {/* Page Content */}
        <main className={isFullScreenView ? '' : 'p-8'}>
          <Routes>
            <Route path="/" element={<ApproachPage />} />
            <Route path="/superintendent" element={<SuperintendentCommandCenter />} />
            <Route path="/supervisor" element={<SupervisorWorkspace />} />
            <Route path="/analyst" element={<AnalystWorkspace />} />
            <Route path="/fi-console" element={<FIConsoleDashboard />} />
            <Route path="/fi-console/guideline/:guideline_id" element={<GuidelineDetailPage />} />
            <Route path="/overview" element={<SupervisoryOverview />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/approach" element={<ApproachPage />} />
            <Route path="/console-stats" element={<ConsoleStatsPage />} />
            <Route path="/visitor-analytics" element={<VisitorAnalyticsPage />} />
            <Route path="/models" element={<ModelsPage />} />
            <Route path="/climate" element={<ClimatePage />} />
            <Route path="/incidents" element={<IncidentsPage />} />
            <Route path="/vendors" element={<VendorsPage />} />
            <Route path="/returns" element={<ReturnsPage />} />
            <Route path="/knowledge" element={<KnowledgePage />} />
            <Route path="/guidance" element={<GuidancePage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/memory-graph" element={<MemoryGraphPage />} />
            <Route path="/holdco" element={<HoldcoPage />} />
            <Route path="/panago" element={<PanagoPage />} />
            <Route path="/phantom-screens" element={<PhantomScreensPage />} />
            <Route path="/apex-food" element={<ApexFoodPage />} />
            <Route path="/daa" element={<DAAPage />} />
          </Routes>
        </main>
      </div>

      {/* Floating Console Button */}
      {!isConsoleOpen && (
        <button
          onClick={() => setIsConsoleOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-blue-700 text-white rounded-full shadow-2xl hover:bg-blue-800 transition-all duration-200 flex items-center justify-center z-40 hover:scale-110"
          title="Open Console"
        >
          <span className="text-2xl">💬</span>
        </button>
      )}

      {/* Console Panel */}
      <ConsolePanel isOpen={isConsoleOpen} onClose={() => setIsConsoleOpen(false)} />

      {/* Command Palette (Global) */}
      <CommandPalette />
    </div>
  );
}

function App() {
  return (
    <MantineProvider theme={theme}>
      <Router>
        <FilterProvider>
          <AppLayout />
        </FilterProvider>
      </Router>
    </MantineProvider>
  );
}

export default App;
